import { query } from '@angular/animations';
import { CommonModule, DatePipe, JsonPipe, NgFor, ɵnormalizeQueryParams } from '@angular/common';
import { Component, DestroyRef, NgModule, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { IgxGridComponent, IgxTabsModule } from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { environment } from 'src/environments/environment';
import { IBuildCompanyInvoiceRequest, IPrelimInvoiceListResponse } from 'src/app/shared/interfaces';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule, MatListOption } from '@angular/material/list';

@Component({
  selector: 'app-generate-gb',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    HeaderTitleLotDataComponent,
    MatSnackBarModule,
    MatProgressBarModule,
    HeaderTitleLotDataComponent,
    MatTableModule,
    MatCheckboxModule,
    FormsModule,
    MatListModule,
    MatListOption,
  ],
  templateUrl: './generate-gb.component.html',
  styleUrl: './generate-gb.component.scss',
})

/* * * * * * * *
  Steps to create prelims - basic one month
  1) Purge all existing Preliminary GB Invoices
  2) Purge all existing Preliminary MI Invoices
  3) Build Prelim Company Invoices
  4) Build Prelim Parker Invoices
  5) Get Prelim invoices and display in grid
  6) For each seleccted invoice to finalize
    a) Finalize Invoice
    b) Log Invoice Finalization
* * * * * * * * */

/* * * *
TO DO
1) Display Appropriate Revenue/Invoice Date number of months from lotsetup defaults
2) Current Month
3) Past Months
4) Create Past Invoices

* * * */
export class GenerateGbComponent implements OnInit {
  prelimInvoiceList: IPrelimInvoiceListResponse[] = [];
  queryInProcess: boolean = false;
  queryCompleted: boolean = false;
  invoiceCompanyData: IBuildCompanyInvoiceRequest = {} as IBuildCompanyInvoiceRequest;
  invoiceParkerData: IBuildCompanyInvoiceRequest = {} as IBuildCompanyInvoiceRequest;
  numberOfCompanyInvoices: number = 0;
  numberOfParkerInvoices: number = 0;
  currentStep: string = 'Purging Previous Invoices';
  dataSource: any;
  selectAllChecked: boolean = false;
  noInvoicesSelectedForFinalization: boolean = true;
  urlDomain: string = '';
  numberOfCompanyInvoicesFinalized: number = 0;
  numberOfParkerInvoicesFinalized: number = 0;
  workflowLogVerbiage: string[] = [];

  displayedColumnsOrders: string[] = [
    'selection',
    'invoiceNumber',
    'invoiceDate',
    'customerName',
    'lineItemSubtotal',
    'invoiceRecipientCount',
    'deliveryType',
    'status',
    'type',
  ];

  invoiceCreateForm = new FormGroup({
    revenueDate: new FormControl('1/1/2025', []),
    invoiceDate: new FormControl('1/1/2025', []),
    currentMonthOnly: new FormControl(false, []),
    multiMonthInvoice: new FormControl(false, []),
    createPastInvoices: new FormControl(false, []),
    multiMonthCount: new FormControl(1, [Validators.min(1), Validators.max(12)]),
    companies: new FormControl('', []),
  });

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  _datePipe = inject(DatePipe);
  _dataService = inject(DataService);
  _destroyRefService = inject(DestroyRef);
  _dialogMessageService = inject(MessageDialogService);

  constructor() {
    this.urlDomain = environment.UrlDomain;
  }

  ngOnInit(): void {}

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onToggleFinalization(invoiceNumber: string): void {
    const index = this.prelimInvoiceList.findIndex((x) => x.invoiceNumber === invoiceNumber);

    this.prelimInvoiceList[index].selectForFinalization = !this.prelimInvoiceList[index].selectForFinalization;

    this.dataSource = new MatTableDataSource(this.prelimInvoiceList);
    this.setReadyToFinalizeButton();
  }

  setReadyToFinalizeButton(): void {
    const index = this.prelimInvoiceList.findIndex((x) => x.selectForFinalization === true);

    this.noInvoicesSelectedForFinalization = index == -1 ? true : false;
  }

  updateInvoiceFinalizedCount(invoiceNumber: string): void {
    const index = this.prelimInvoiceList.findIndex((x) => x.invoiceNumber === invoiceNumber);

    if ((this.prelimInvoiceList[index].customerType = 'C')) {
      this.numberOfCompanyInvoicesFinalized++;
    } else this.numberOfParkerInvoicesFinalized++;
  }

  onToggleSelectAll(): void {
    this.selectAllChecked = !this.selectAllChecked;

    for (let ii = 0; ii < this.prelimInvoiceList.length; ii++) {
      this.prelimInvoiceList[ii].selectForFinalization = this.selectAllChecked;
    }

    this.dataSource = new MatTableDataSource(this.prelimInvoiceList);
    this.setReadyToFinalizeButton();
  }

  addWorkflowLogVerbiage(value: string): void {
    this.workflowLogVerbiage.push(value);
  }

  onGeneratePrelims(): void {
    this.queryInProcess = true;
    const currentDateTime: string = new Date().toDateString() + ' ' + new Date().toTimeString();

    this.prelimInvoiceList = []; //Reset the prelim invoice array

    this.numberOfCompanyInvoices = 0;
    this.numberOfParkerInvoices = 0;
    this.numberOfCompanyInvoicesFinalized = 0;
    this.numberOfParkerInvoicesFinalized = 0;

    this.workflowLogVerbiage = []; //Reset the log array

    this.addWorkflowLogVerbiage(
      `Starting New Prelim Generation for Lot Number ${this._appGeneralState.currentLotNumber} @ ${currentDateTime}`,
    );

    this.addWorkflowLogVerbiage('Clearing previous invoice generation values');

    this.invoiceCompanyData.lotNumber = this._appGeneralState.currentLotNumber;
    this.invoiceCompanyData.userName = this._appGeneralState.userName;
    this.invoiceCompanyData.invoiceDate = new Date(this.invoiceCreateForm.get('invoiceDate').value);
    this.invoiceCompanyData.dueDate = new Date(this.invoiceCreateForm.get('revenueDate').value);

    this.invoiceParkerData.lotNumber = this._appGeneralState.currentLotNumber;
    this.invoiceParkerData.userName = this._appGeneralState.userName;
    this.invoiceParkerData.invoiceDate = new Date(this.invoiceCreateForm.get('invoiceDate').value);
    this.invoiceParkerData.dueDate = new Date(this.invoiceCreateForm.get('revenueDate').value);

    this.addWorkflowLogVerbiage('Purging previous prelimanry invoice values');
    this.deleteExistingGBPrelims(this._appGeneralState.currentLotNumber);
  }

  onClearSearchCriteria(): void {}

  onFinalizeSelectedInvoices(): void {
    const lotNumber: string = this._appGeneralState.currentLotNumber;

    for (let ii = 0; ii < this.prelimInvoiceList.length; ii++) {
      if (this.prelimInvoiceList[ii].selectForFinalization) {
        const invoiceNumber: string = this.prelimInvoiceList[ii].invoiceNumber;
        this.updateInvoiceToFinalizedStatus(lotNumber, invoiceNumber);
        this.addWorkflowLogVerbiage(`Invoice Number: ${invoiceNumber} has been finalized`);
      }
    }

    this._dialogMessageService.snackBarTopCenterTimed('The invoice(s) have been finalized', 5000);
  }

  setInvoiceToFinalizedStatus(invoiceNumber: string): void {
    const index = this.prelimInvoiceList.findIndex((x) => x.invoiceNumber === invoiceNumber);
    this.prelimInvoiceList[index].selectForFinalization = false;
    this.prelimInvoiceList[index].isPrelim = false;
  }

  logFinalizedInvoice(invoiceNumber: string): void {
    this.currentStep = 'Logging finalized Invoice Status';
    this._dataService
      .logFinalizedInvoice(invoiceNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.setInvoiceToFinalizedStatus(invoiceNumber);
        },
        error: (err: any) => {},
        complete: () => {
          this.setReadyToFinalizeButton();
        },
      });
  }

  updateInvoiceToFinalizedStatus(lotNumber: string, invoiceNumber: string): void {
    this.currentStep = 'Updating Invoice to Finalize Status';
    this._dataService
      .updateInvoiceStatusToFinalized(lotNumber, invoiceNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.setInvoiceToFinalizedStatus(invoiceNumber);
        },
        error: (err: any) => {},
        complete: () => {
          this.updateInvoiceFinalizedCount(invoiceNumber);
          this.logFinalizedInvoice(invoiceNumber);
        },
      });
  }

  getPrelimInvoiceList() {
    this.currentStep = 'Getting Prelim Invoice List';
    this._dataService
      .getCompanyParkerPrelimInvoiceList(this._appGeneralState.currentLotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.prelimInvoiceList = res;
        },
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
        },
        complete: () => {
          this.currentStep = 'Getting Prelim Invoice List';
          this.queryInProcess = false;
          this.queryCompleted = true;

          for (let ii = 0; ii < this.prelimInvoiceList.length; ii++) {
            this.prelimInvoiceList[ii].isPrelim = true;
          }

          this.dataSource = new MatTableDataSource(this.prelimInvoiceList);
          this.addWorkflowLogVerbiage(`${this.prelimInvoiceList.length} Preliminary Invoice List Generated`);
          this._dialogMessageService.snackBarTopCenterTimed(
            `Invoices Generated.  Company: ${this.numberOfCompanyInvoices}, Parker: ${this.numberOfParkerInvoices}`,
            7000,
          );
        },
      });
  }

  createParkerProfileInvoices() {
    this.currentStep = 'Generating Parker Invoices';
    this._dataService
      .createParkerPrelimInvoices(this.invoiceParkerData)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.numberOfParkerInvoices = res;
        },
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
        },
        complete: () => {
          this.currentStep = 'Prelim Invoice Creation Completed';
          this.addWorkflowLogVerbiage(`${this.numberOfParkerInvoices} Parker Prelim Invoices Generated`);
          this.getPrelimInvoiceList();
        },
      });
  }

  //Builds the prelim invoices for companies
  createCompanyProfileInvoices() {
    this.currentStep = 'Generating Company Invoices';
    this._dataService
      .createCompanyPrelimInvoices(this.invoiceCompanyData)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.numberOfCompanyInvoices = res;
        },
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
        },
        complete: () => {
          this.createParkerProfileInvoices();
          this.addWorkflowLogVerbiage(`${this.numberOfCompanyInvoices} Company Prelim Invoices Generated`);
        },
      });
  }

  //Checks to see if all invoices have been prepared and finalized.
  //Returns True if finalized.  We can stop processing if True.
  areAllInvoicesFinalized(lotNumber: string) {
    const invoiceDate: string = '1-1-2025';
    const dueDate: string = '1-1-2025';

    this._dataService
      .getStatusAreAllInvoiceFinalized(lotNumber, invoiceDate, dueDate)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
        },
        complete: () => {
          this.createCompanyProfileInvoices();
        },
      });
  }

  deleteExistingGBPrelims(lotNumber: string) {
    this._dataService
      .deleteExistingGbPrelimInvoices(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
        },
        complete: () => {
          this.deleteExistingMiPrelims(lotNumber);
        },
      });
  }

  deleteExistingMiPrelims(lotNumber: string) {
    this._dataService
      .deleteExistingMiPrelimInvoices(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {},
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
        },
        complete: () => {
          this.areAllInvoicesFinalized(lotNumber);
        },
      });
  }
}
